import {
  Box,
  Chip,
  Divider,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { useState, useMemo, useCallback, memo } from 'react'
import toast from 'react-hot-toast'
import {
  BaseMaterialCleanChangeoverRowType,
  ProductCleanChangeoverRowType,
  SchedulerTabsProps,
} from './Scheduler.types'
import {
  useScheduleProductionRequirements,
  useUpdateProductionRequirements,
} from './Scheduler.data'

// ─── Types ───────────────────────────────────────────────────────────────────

type CellKey = `${string}|${string}`

type SelectedCell = {
  row: string
  col: string
}

type ChangeoverRecord = {
  id: number
  remove_clean: string
  other_changeover: string
  changeover_cost: string
  changeover_environmental_cost: string
}

// ─── Editable field ──────────────────────────────────────────────────────────

const EditableField = ({
  label,
  value,
  onChange,
}: {
  label: string
  value: string
  onChange: (v: string) => void
}) => (
  <TextField
    label={label}
    value={value}
    onChange={(e) => onChange(e.target.value)}
    size="small"
    variant="outlined"
    sx={{
      minWidth: 140,
      '& .MuiInputBase-input': { fontSize: '0.75rem', py: '6px' },
      '& .MuiInputLabel-root': { fontSize: '0.75rem' },
    }}
  />
)

// ─── Color helpers ───────────────────────────────────────────────────────────

const computePercentileThresholds = (costs: number[]): [number, number] => {
  if (costs.length === 0) return [0, 0]
  const sorted = [...costs].sort((a, b) => a - b)
  const p33 = sorted[Math.floor(sorted.length * 0.33)] ?? 0
  const p66 = sorted[Math.floor(sorted.length * 0.66)] ?? 0
  return [p33, p66]
}

const getCellColor = (
  cost: number | undefined,
  isSelf: boolean,
  thresholds: [number, number]
): string => {
  if (isSelf) return '#eeeeee'
  if (cost === undefined) return '#f5f5f5'
  const [low, high] = thresholds
  if (cost <= low) return '#e8f5e9'
  if (cost <= high) return '#fff8e1'
  return '#ffebee'
}

// ─── Matrix component (memoised) ─────────────────────────────────────────────

type MatrixProps = {
  rowLabels: string[]
  colLabels: string[]
  cellMap: Map<CellKey, ChangeoverRecord>
  thresholds: [number, number]
  selectedCell: SelectedCell | null
  onCellClick: (row: string, col: string) => void
}

const TransitionMatrix = memo(
  ({
    rowLabels,
    colLabels,
    cellMap,
    thresholds,
    selectedCell,
    onCellClick,
  }: MatrixProps) => {
    if (rowLabels.length === 0) {
      return (
        <Typography variant="body2" sx={{ p: 2, color: 'text.secondary' }}>
          No transitions defined.
        </Typography>
      )
    }

    return (
      <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 320px)' }}>
        <table
          style={{
            borderCollapse: 'collapse',
            fontSize: '0.7rem',
            tableLayout: 'fixed',
          }}
        >
          <thead>
            <tr>
              <th
                style={{
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  background: '#fafafa',
                  padding: '2px 4px',
                  minWidth: 80,
                  maxWidth: 100,
                  fontSize: '0.65rem',
                  borderBottom: '1px solid #ddd',
                  borderRight: '1px solid #ddd',
                }}
              >
                prev \ next
              </th>
              {colLabels.map((col) => (
                <th
                  key={col}
                  style={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    background: '#fafafa',
                    padding: '2px 3px',
                    minWidth: 56,
                    maxWidth: 80,
                    fontSize: '0.65rem',
                    borderBottom: '1px solid #ddd',
                    borderRight: '1px solid #eee',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    textAlign: 'center',
                    writingMode: 'vertical-rl',
                    transform: 'rotate(180deg)',
                    height: 80,
                  }}
                >
                  <Tooltip title={col} placement="top">
                    <span>{col}</span>
                  </Tooltip>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rowLabels.map((row) => (
              <tr key={row}>
                <td
                  style={{
                    position: 'sticky',
                    left: 0,
                    zIndex: 1,
                    background: '#fafafa',
                    padding: '2px 4px',
                    fontSize: '0.65rem',
                    fontWeight: 600,
                    borderRight: '1px solid #ddd',
                    borderBottom: '1px solid #eee',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: 100,
                  }}
                >
                  <Tooltip title={row} placement="left">
                    <span>{row}</span>
                  </Tooltip>
                </td>
                {colLabels.map((col) => {
                  const key: CellKey = `${row}|${col}`
                  const record = cellMap.get(key)
                  const isSelf = row === col
                  const cost = record
                    ? parseFloat(record.changeover_cost)
                    : undefined
                  const isSelected =
                    selectedCell?.row === row && selectedCell?.col === col
                  const bg = getCellColor(
                    isNaN(cost as number) ? undefined : cost,
                    isSelf,
                    thresholds
                  )

                  return (
                    <td
                      key={col}
                      onClick={
                        isSelf || !record
                          ? undefined
                          : () => onCellClick(row, col)
                      }
                      style={{
                        background: bg,
                        padding: '1px 2px',
                        textAlign: 'center',
                        fontSize: '0.65rem',
                        borderBottom: '1px solid #eee',
                        borderRight: '1px solid #eee',
                        cursor: isSelf || !record ? 'default' : 'pointer',
                        outline: isSelected ? '2px solid #1976d2' : 'none',
                        outlineOffset: -1,
                        minWidth: 56,
                        userSelect: 'none',
                      }}
                    >
                      {isSelf ? (
                        <span style={{ color: '#bbb' }}>&mdash;</span>
                      ) : record ? (
                        <Tooltip
                          title={`Cost: ${record.changeover_cost} | Env: ${record.changeover_environmental_cost}`}
                          placement="top"
                        >
                          <span>{record.changeover_cost}</span>
                        </Tooltip>
                      ) : null}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    )
  }
)

// ─── Main component ──────────────────────────────────────────────────────────

export const ScheduleChangeoverView = ({
  schedulerId,
  iteration,
}: SchedulerTabsProps) => {
  const [activeTab, setActiveTab] = useState(0)
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null)
  const [selectedCell, setSelectedCell] = useState<SelectedCell | null>(null)
  const [editValues, setEditValues] = useState<ChangeoverRecord | null>(null)

  // ── Data fetching ────────────────────────────────────────────────────────

  const { data: productResponse = { data: [] }, isLoading: productLoading } =
    useScheduleProductionRequirements(
      schedulerId,
      'product-clean-changeover',
      iteration
    )
  const productData: ProductCleanChangeoverRowType[] = useMemo(() => {
    const d = productResponse?.data
    return (Array.isArray(d) ? d : d?.data) ?? []
  }, [productResponse])

  const { data: baseResponse = { data: [] }, isLoading: baseLoading } =
    useScheduleProductionRequirements(
      schedulerId,
      'base-material-clean-changeover',
      iteration
    )
  const baseData: BaseMaterialCleanChangeoverRowType[] = useMemo(() => {
    const d = baseResponse?.data
    return (Array.isArray(d) ? d : d?.data) ?? []
  }, [baseResponse])

  const onSaveSuccess = useCallback(
    () => toast.success('Changeover updated'),
    []
  )
  const onSaveFailure = useCallback(
    () => toast.error('Failed to update changeover'),
    []
  )
  const { mutate: saveData } = useUpdateProductionRequirements(
    onSaveSuccess,
    onSaveFailure
  )

  // ── Derived data: product tab ────────────────────────────────────────────

  const productGroups = useMemo(() => {
    const map = new Map<string, ProductCleanChangeoverRowType[]>()
    productData.forEach((r) => {
      const group = map.get(r.line_code) ?? []
      group.push(r)
      map.set(r.line_code, group)
    })
    return map
  }, [productData])

  // ── Derived data: base material tab ──────────────────────────────────────

  const baseGroups = useMemo(() => {
    const map = new Map<string, BaseMaterialCleanChangeoverRowType[]>()
    baseData.forEach((r) => {
      const group = map.get(r.kitchen_code) ?? []
      group.push(r)
      map.set(r.kitchen_code, group)
    })
    return map
  }, [baseData])

  // ── Active group list and records ────────────────────────────────────────

  const isProduct = activeTab === 0
  const groupNames = useMemo(
    () =>
      Array.from(isProduct ? productGroups.keys() : baseGroups.keys()).sort(),
    [isProduct, productGroups, baseGroups]
  )

  // Auto-select first group when switching tabs or data loads
  const effectiveGroup = useMemo(() => {
    if (selectedGroup && groupNames.includes(selectedGroup))
      return selectedGroup
    return groupNames[0] ?? null
  }, [selectedGroup, groupNames])

  const activeRecords = useMemo(() => {
    if (!effectiveGroup) return []
    if (isProduct) return productGroups.get(effectiveGroup) ?? []
    return baseGroups.get(effectiveGroup) ?? []
  }, [effectiveGroup, isProduct, productGroups, baseGroups])

  // ── Matrix data ──────────────────────────────────────────────────────────

  const { rowLabels, colLabels, cellMap, thresholds } = useMemo(() => {
    const rowSet = new Set<string>()
    const colSet = new Set<string>()
    const map = new Map<CellKey, ChangeoverRecord>()
    const costs: number[] = []

    activeRecords.forEach((r) => {
      const prev = isProduct
        ? (r as ProductCleanChangeoverRowType).previous_product_code
        : (r as BaseMaterialCleanChangeoverRowType).previous_material_code
      const next = isProduct
        ? (r as ProductCleanChangeoverRowType).next_product_code
        : (r as BaseMaterialCleanChangeoverRowType).next_base_material_code

      rowSet.add(prev)
      colSet.add(next)

      const key: CellKey = `${prev}|${next}`
      map.set(key, {
        id: r.id,
        remove_clean: r.remove_clean,
        other_changeover: r.other_changeover,
        changeover_cost: r.changeover_cost,
        changeover_environmental_cost: r.changeover_environmental_cost,
      })

      const c = parseFloat(r.changeover_cost)
      if (!isNaN(c)) costs.push(c)
    })

    // Use union of rows and cols for a square matrix
    const allLabels = Array.from(new Set([...rowSet, ...colSet])).sort()
    return {
      rowLabels: allLabels,
      colLabels: allLabels,
      cellMap: map,
      thresholds: computePercentileThresholds(costs),
    }
  }, [activeRecords, isProduct])

  // ── Handlers ─────────────────────────────────────────────────────────────

  const handleTabChange = useCallback((_: unknown, v: number) => {
    setActiveTab(v)
    setSelectedGroup(null)
    setSelectedCell(null)
    setEditValues(null)
  }, [])

  const handleGroupSelect = useCallback((name: string) => {
    setSelectedGroup(name)
    setSelectedCell(null)
    setEditValues(null)
  }, [])

  const handleCellClick = useCallback(
    (row: string, col: string) => {
      const key: CellKey = `${row}|${col}`
      const record = cellMap.get(key)
      if (!record) return
      setSelectedCell({ row, col })
      // Find the full row from activeRecords so we can save all fields
      const fullRow = activeRecords.find((r) => r.id === record.id)
      setEditValues({
        ...record,
        _fullRow: fullRow as unknown as Record<string, unknown>,
      } as any)
    },
    [cellMap, activeRecords]
  )

  const handleFieldChange = useCallback(
    (field: keyof ChangeoverRecord, value: string) => {
      setEditValues((prev) => (prev ? { ...prev, [field]: value } : null))
    },
    []
  )

  const handleSave = useCallback(() => {
    if (!editValues || !selectedCell) return
    const api = isProduct
      ? 'product-clean-changeover'
      : 'base-material-clean-changeover'
    // Merge editable fields back into the full row for the API
    const fullRow = (editValues as any)._fullRow || {}
    const saveRow = {
      ...fullRow,
      remove_clean: editValues.remove_clean,
      other_changeover: editValues.other_changeover,
      changeover_cost: editValues.changeover_cost,
      changeover_environmental_cost: editValues.changeover_environmental_cost,
    }
    saveData({
      data: { [editValues.id]: saveRow } as Record<number, any>,
      schedulerId,
      API: api,
      iteration,
    })
  }, [editValues, selectedCell, isProduct, saveData, schedulerId, iteration])

  // ── Loading state ────────────────────────────────────────────────────────

  const isLoading = productLoading || baseLoading

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <Paper
      variant="outlined"
      sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      {/* Sub-tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        sx={{
          minHeight: 36,
          '& .MuiTab-root': {
            minHeight: 36,
            fontSize: '0.75rem',
            textTransform: 'none',
          },
        }}
      >
        <Tab label="Product Changeovers" />
        <Tab label="Base Material Changeovers" />
      </Tabs>
      <Divider />

      <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* Sidebar */}
        <Box
          sx={{
            width: 200,
            minWidth: 200,
            borderRight: '1px solid #e0e0e0',
            overflow: 'auto',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              px: 1.5,
              pt: 1,
              pb: 0.5,
              display: 'block',
              fontWeight: 600,
              color: 'text.secondary',
              fontSize: '0.7rem',
            }}
          >
            {isProduct ? 'Lines' : 'Kitchens'}
          </Typography>
          <Divider />
          <List dense disablePadding>
            {groupNames.map((name) => {
              const count = isProduct
                ? (productGroups.get(name)?.length ?? 0)
                : (baseGroups.get(name)?.length ?? 0)
              return (
                <ListItemButton
                  key={name}
                  selected={effectiveGroup === name}
                  onClick={() => handleGroupSelect(name)}
                  sx={{ py: 0.5, px: 1.5 }}
                >
                  <ListItemText
                    primary={name}
                    primaryTypographyProps={{ fontSize: '0.75rem' }}
                  />
                  <Chip
                    label={count}
                    size="small"
                    sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                  />
                </ListItemButton>
              )
            })}
            {groupNames.length === 0 && !isLoading && (
              <Typography
                variant="caption"
                sx={{ p: 1.5, display: 'block', color: 'text.disabled' }}
              >
                No data available
              </Typography>
            )}
          </List>
        </Box>

        {/* Main area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          {/* Summary bar */}
          {effectiveGroup && (
            <Box
              sx={{
                px: 1.5,
                py: 0.75,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: '1px solid #eee',
                bgcolor: '#fafafa',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontSize: '0.75rem', fontWeight: 500 }}
              >
                {activeRecords.length} transitions defined for{' '}
                <strong>{effectiveGroup}</strong>
              </Typography>
              <Box sx={{ ml: 'auto', display: 'flex', gap: 1 }}>
                <Tooltip title="Low cost">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      bgcolor: '#e8f5e9',
                      border: '1px solid #ccc',
                      borderRadius: '2px',
                    }}
                  />
                </Tooltip>
                <Tooltip title="Medium cost">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      bgcolor: '#fff8e1',
                      border: '1px solid #ccc',
                      borderRadius: '2px',
                    }}
                  />
                </Tooltip>
                <Tooltip title="High cost">
                  <Box
                    sx={{
                      width: 14,
                      height: 14,
                      bgcolor: '#ffebee',
                      border: '1px solid #ccc',
                      borderRadius: '2px',
                    }}
                  />
                </Tooltip>
              </Box>
            </Box>
          )}

          {/* Matrix */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
            {isLoading ? (
              <Typography
                variant="body2"
                sx={{ p: 2, color: 'text.secondary', fontSize: '0.75rem' }}
              >
                Loading changeover data...
              </Typography>
            ) : effectiveGroup ? (
              <TransitionMatrix
                rowLabels={rowLabels}
                colLabels={colLabels}
                cellMap={cellMap}
                thresholds={thresholds}
                selectedCell={selectedCell}
                onCellClick={handleCellClick}
              />
            ) : (
              <Typography
                variant="body2"
                sx={{ p: 2, color: 'text.disabled', fontSize: '0.75rem' }}
              >
                Select a {isProduct ? 'line' : 'kitchen'} to view the transition
                matrix.
              </Typography>
            )}
          </Box>

          {/* Cell detail panel */}
          {selectedCell && editValues && (
            <>
              <Divider />
              <Paper
                elevation={0}
                sx={{
                  px: 2,
                  py: 1.5,
                  bgcolor: '#fafafa',
                  borderTop: '1px solid #e0e0e0',
                }}
              >
                <Box
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    {selectedCell.row}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: 'text.secondary', fontSize: '0.7rem' }}
                  >
                    &rarr;
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ fontSize: '0.75rem', fontWeight: 600 }}
                  >
                    {selectedCell.col}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    gap: 1.5,
                    flexWrap: 'wrap',
                    alignItems: 'flex-end',
                  }}
                >
                  <Box sx={{ minWidth: 90 }}>
                    <Typography
                      sx={{
                        fontSize: '0.6rem',
                        color: 'text.secondary',
                        mb: 0.25,
                      }}
                    >
                      Remove Clean
                    </Typography>
                    <Chip
                      label={
                        editValues.remove_clean === 'True' ||
                        editValues.remove_clean === 'true' ||
                        editValues.remove_clean === '1'
                          ? 'Yes'
                          : 'No'
                      }
                      size="small"
                      color={
                        editValues.remove_clean === 'True' ||
                        editValues.remove_clean === 'true' ||
                        editValues.remove_clean === '1'
                          ? 'error'
                          : 'default'
                      }
                      onClick={() => {
                        const current =
                          editValues.remove_clean === 'True' ||
                          editValues.remove_clean === 'true' ||
                          editValues.remove_clean === '1'
                        handleFieldChange(
                          'remove_clean',
                          current ? 'False' : 'True'
                        )
                      }}
                      sx={{
                        cursor: 'pointer',
                        height: 28,
                        fontSize: '0.7rem',
                        fontWeight: 600,
                      }}
                    />
                  </Box>
                  <EditableField
                    label="Changeover Time (h)"
                    value={editValues.other_changeover}
                    onChange={(v) => handleFieldChange('other_changeover', v)}
                  />
                  <EditableField
                    label="Changeover Cost"
                    value={editValues.changeover_cost}
                    onChange={(v) => handleFieldChange('changeover_cost', v)}
                  />
                  <EditableField
                    label="Env. Cost"
                    value={editValues.changeover_environmental_cost}
                    onChange={(v) =>
                      handleFieldChange('changeover_environmental_cost', v)
                    }
                  />
                  <Chip
                    label="Save"
                    color="primary"
                    size="small"
                    onClick={handleSave}
                    sx={{
                      height: 30,
                      fontSize: '0.75rem',
                      cursor: 'pointer',
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </Paper>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  )
}
